import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { SpriteManager } from "@babylonjs/core/Sprites/spriteManager";
import { Sprite } from "@babylonjs/core/Sprites/sprite";
import "@babylonjs/core/Sprites/spriteSceneComponent";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { TrapPhase, type ArenaBox, type ArenaDefinition, type ArenaOval } from "@arena/shared";
import { TEXELS_PER_METRE, surfaceRepeat, surfaceTexture, type SurfaceKind } from "./pixelTextures.ts";
import type { Hd2dWorld } from "./hd2d.ts";

/**
 * The Colosseo della Storia drawn for the HD-2D look (D38): the solids of the
 * arena definition stay invisible, the eye sees an oval of sand, the moat,
 * the podium wall and the stands full of people of every age. The near side
 * of the stands fades out when the camera goes behind it.
 */

const SEGMENTS = 144;
const SECTORS = 12;
/** Stands: steps of the cavea, from the podium wall outwards. */
const TIERS = 10;
const TIER_RISE = 0.6;
const TIER_RUN = 1.15;
const PARAPET = 0.5;
const FACADE = 4;
/** Metres of world per texture repeat (textures are 16 texels per metre). */
/** Metres covered by one repeat of the 64-texel sand. */
const SAND_TILE = 64 / TEXELS_PER_METRE;

interface ProfilePoint {
  /** Distance outwards from the podium wall ellipse. */
  d: number;
  y: number;
}

interface Sector {
  /** Centre angle on the ellipse parameter. */
  angle: number;
  materials: StandardMaterial[];
  alpha: number;
}

interface Spectator {
  sprite: Sprite;
  baseY: number;
  phase: number;
  speed: number;
  cell: number;
  sector: number;
}

interface Hatch {
  leaves: TransformNode[];
}

const PEOPLE_VARIANTS = 12;
const EMPEROR_CELL = PEOPLE_VARIANTS * 2;

export class ColosseoView {
  /** Meshes that receive the sun's shadows. */
  private readonly receivers: Mesh[] = [];
  /** Meshes that cast them (small things near the action, not the stands). */
  private readonly casters: Mesh[] = [];
  private readonly torchSpots: Vector3[] = [];
  private readonly sectors: Sector[] = [];
  private readonly spectators: Spectator[] = [];
  private readonly hatches: Hatch[] = [];
  private readonly levers: TransformNode[] = [];
  private readonly leverRings: Mesh[] = [];
  private readonly hatchMaterial: StandardMaterial;
  private readonly ringMaterial: StandardMaterial;
  private readonly oval: ArenaOval;
  private time = 0;
  private openAngle = 0;
  private leverAngle = 0.6;
  private excitement = 0.3;
  private boost = 0;

  constructor(scene: Scene, arena: ArenaDefinition) {
    const oval = arena.oval;
    if (!oval) throw new Error(`Arena ${arena.id}: manca "oval" per il Colosseo`);
    this.oval = oval;
    const hatchBoxes = arena.solids.filter((b) => b.kind === "hatch");
    const hole = bounds(hatchBoxes);

    // Sand with the hatch hole, its rim down into the moat, the dark bottom of everything.
    const sand = this.mesh(scene, "sand", sandMesh(oval, hole), "sand");
    this.receivers.push(sand);
    const rim = this.mesh(scene, "sand-rim", ovalStrip(oval.a, oval.b, [{ d: 0, y: 0 }, { d: 0, y: -2.5 }], 0, 1, SEGMENTS), "wall");
    this.receivers.push(rim);
    const abyss = CreateDisc("abyss", { radius: 1, tessellation: 64 }, scene);
    abyss.rotation.x = Math.PI / 2;
    abyss.scaling.set(oval.a + oval.moat + 1, oval.b + oval.moat + 1, 1);
    abyss.position.y = -7;
    abyss.material = flatMaterial(scene, "abyss", "#0b0709");
    abyss.isPickable = false;

    // The pit under the hatches (the hypogeum): seen from inside, warm light at the bottom.
    if (hole) {
      const pit = CreateBox("pit", { width: hole.max.x - hole.min.x, height: 6, depth: hole.max.z - hole.min.z, sideOrientation: Mesh.BACKSIDE }, scene);
      pit.position.set((hole.min.x + hole.max.x) / 2, -3.3, (hole.min.z + hole.max.z) / 2);
      const pitMaterial = this.textured(scene, "pit", "brick", 1, 1);
      // Lit from below by the hypogeum's torches.
      pitMaterial.emissiveColor = Color3.FromHexString("#3a1c12");
      pitMaterial.diffuseColor = Color3.FromHexString("#5a4038");
      pit.material = pitMaterial;
      pit.isPickable = false;
      const glow = CreateDisc("pit-glow", { radius: 1, tessellation: 24 }, scene);
      glow.rotation.x = Math.PI / 2;
      glow.scaling.set((hole.max.x - hole.min.x) * 0.45, (hole.max.z - hole.min.z) * 0.45, 1);
      glow.position.set(pit.position.x, -6.2, pit.position.z);
      const glowMaterial = flatMaterial(scene, "pit-glow", "#000000");
      glowMaterial.emissiveColor = Color3.FromHexString("#c0501a");
      glow.material = glowMaterial;
    }

    // Hatches: two leaves each, hinged on the outer edges, falling open downwards.
    this.hatchMaterial = this.textured(scene, "hatch", "wood", 1, 1);
    for (const box of hatchBoxes) this.hatches.push(this.buildHatch(scene, box));

    // Levers of the trap, with a pulsing ring on the sand while they can be pulled.
    this.ringMaterial = flatMaterial(scene, "lever-ring", "#000000");
    this.ringMaterial.emissiveColor = Color3.FromHexString("#ffcc55");
    this.ringMaterial.alpha = 0.5;
    for (const lever of arena.traps?.flatMap((t) => t.levers) ?? []) this.buildLever(scene, lever);

    // Podium wall, parapet, stands and facade, in sectors that can fade one by one.
    const a = oval.a + oval.moat;
    const b = oval.b + oval.moat;
    const profile = standsProfile(oval.wall);
    for (let i = 0; i < SECTORS; i++) {
      const from = i / SECTORS;
      const to = (i + 1) / SECTORS;
      const span = Math.round(SEGMENTS / SECTORS);
      const wall = this.mesh(scene, `wall-${i}`, ovalStrip(a, b, [{ d: 0, y: -7 }, { d: 0, y: oval.wall }], from, to, span), "stone");
      const stands = this.mesh(scene, `stands-${i}`, ovalStrip(a, b, profile.stands, from, to, span), "stone");
      const facade = this.mesh(scene, `facade-${i}`, ovalStrip(a, b, profile.facade, from, to, span), "arches");
      this.receivers.push(wall, stands);
      this.sectors.push({
        angle: ((from + to) / 2) * Math.PI * 2,
        materials: [wall.material as StandardMaterial, stands.material as StandardMaterial, facade.material as StandardMaterial],
        alpha: 1,
      });
    }

    // Torches on the parapet.
    for (let k = 0; k < 6; k++) {
      const t = (k / 6) * Math.PI * 2 + Math.PI / 6;
      this.torchSpots.push(new Vector3(Math.cos(t) * (a + 0.15), oval.wall + 0.45, Math.sin(t) * (b + 0.15)));
    }

    this.buildEmperorBox(scene, b);
    this.buildCrowd(scene, a, b);
  }

  /** What the HD-2D atmosphere needs from this arena. */
  hd2dWorld(): Hd2dWorld {
    return {
      casters: this.casters,
      receivers: this.receivers,
      torches: this.torchSpots,
      extent: { x: this.oval.a, z: this.oval.b },
    };
  }

  /** Something spectacular happened: the crowd jumps up for a moment. */
  cheer(amount = 1): void {
    this.boost = Math.max(this.boost, amount);
  }

  /**
   * One frame: trap phase from the server state, applause meter (0–1), and the
   * camera position to fade the stands that stand between it and the arena.
   */
  update(dt: number, trap: number, crowd: number, camera: Vector3): void {
    this.time += dt;
    const blend = (rate: number) => 1 - Math.exp(-dt * rate);

    // Hatches: they flash while warning, fall open, close again.
    const open = trap === TrapPhase.Open;
    this.openAngle += ((open ? Math.PI / 2 : 0) - this.openAngle) * blend(open ? 14 : 5);
    for (const hatch of this.hatches) {
      hatch.leaves[0]!.rotation.z = -this.openAngle;
      hatch.leaves[1]!.rotation.z = this.openAngle;
    }
    const warning = trap === TrapPhase.Warning;
    const pulse = warning ? (Math.sin(this.time * 18) > 0 ? 1 : 0.25) : 0;
    this.hatchMaterial.emissiveColor.set(0.9 * pulse, 0.35 * pulse, 0.05 * pulse);

    // Levers: pulled while the trap runs, back up when ready.
    const leverTarget = trap === TrapPhase.Ready ? 0.6 : -0.6;
    this.leverAngle += (leverTarget - this.leverAngle) * blend(trap === TrapPhase.Ready ? 2 : 16);
    for (const lever of this.levers) lever.rotation.z = this.leverAngle;
    const ready = trap === TrapPhase.Ready;
    this.ringMaterial.alpha = ready ? 0.25 + 0.25 * (0.5 + 0.5 * Math.sin(this.time * 4)) : 0;
    for (const ring of this.leverRings) ring.isVisible = ready;

    // Stands: the sectors on the camera's side fade when the camera is behind them.
    const a = this.oval.a + this.oval.moat;
    const b = this.oval.b + this.oval.moat;
    const outside = (camera.x / a) ** 2 + (camera.z / b) ** 2 > 0.8;
    const cameraAngle = Math.atan2(camera.z / b, camera.x / a);
    for (const sector of this.sectors) {
      const near = outside && Math.abs(angleDiff(sector.angle, cameraAngle)) < 0.9;
      const target = near ? 0.08 : 1;
      sector.alpha += (target - sector.alpha) * blend(8);
      for (const m of sector.materials) m.alpha = sector.alpha;
    }

    // Spectators: they bob with the applause meter, jump on a cheer.
    this.boost = Math.max(0, this.boost - dt * 0.5);
    this.excitement += (Math.min(1, 0.25 + crowd * 0.75 + this.boost) - this.excitement) * blend(3);
    const e = this.excitement;
    for (const s of this.spectators) {
      const wave = Math.sin(this.time * s.speed + s.phase);
      s.sprite.position.y = s.baseY + Math.max(0, wave) * (0.05 + 0.2 * e);
      if (s.cell !== EMPEROR_CELL) s.sprite.cellIndex = e > 0.55 && wave > 0.2 ? s.cell + 1 : s.cell;
      const alpha = this.sectors[s.sector]!.alpha;
      s.sprite.color.a = alpha;
      s.sprite.isVisible = alpha > 0.15;
    }
  }

  // -------------------------------------------------------------------------

  private mesh(scene: Scene, name: string, data: VertexData, kind: SurfaceKind): Mesh {
    const mesh = new Mesh(name, scene);
    data.applyToMesh(mesh);
    mesh.material = this.textured(scene, name, kind, 1, 1);
    mesh.isPickable = false;
    mesh.freezeWorldMatrix();
    return mesh;
  }

  private textured(scene: Scene, name: string, kind: SurfaceKind, uScale: number, vScale: number): StandardMaterial {
    const material = new StandardMaterial(name, scene);
    const texture = surfaceTexture(scene, kind);
    texture.uScale = uScale * surfaceRepeat(kind);
    texture.vScale = vScale * surfaceRepeat(kind);
    material.diffuseTexture = texture;
    material.specularColor = Color3.Black();
    material.backFaceCulling = false;
    material.twoSidedLighting = true;
    material.maxSimultaneousLights = 8;
    return material;
  }

  private buildHatch(scene: Scene, box: ArenaBox): Hatch {
    const width = box.max.x - box.min.x;
    const depth = box.max.z - box.min.z;
    const leaves: TransformNode[] = [];
    for (const side of [0, 1]) {
      const hinge = new TransformNode(`${box.id}-hinge-${side}`, scene);
      hinge.position.set(side === 0 ? box.min.x : box.max.x, -0.02, (box.min.z + box.max.z) / 2);
      const leaf = CreateBox(`${box.id}-leaf-${side}`, { width: width / 2 - 0.04, height: 0.1, depth: depth - 0.06 }, scene);
      leaf.parent = hinge;
      leaf.position.set(side === 0 ? width / 4 : -width / 4, -0.03, 0);
      leaf.material = this.hatchMaterial;
      leaf.isPickable = false;
      this.casters.push(leaf);
      leaf.receiveShadows = true;
      // Iron bands across the leaf.
      for (const dz of [-depth / 3, depth / 3]) {
        const band = CreateBox(`${box.id}-band`, { width: width / 2 - 0.08, height: 0.02, depth: 0.08 }, scene);
        band.parent = leaf;
        band.position.set(0, 0.06, dz);
        band.material = flatMaterial(scene, "iron", "#2c2a30");
        band.isPickable = false;
      }
      leaves.push(hinge);
    }
    return { leaves };
  }

  private buildLever(scene: Scene, at: { x: number; y: number; z: number }): void {
    const base = CreateBox("lever-base", { width: 0.6, height: 0.45, depth: 0.6 }, scene);
    base.position.set(at.x, at.y + 0.225, at.z);
    base.material = this.textured(scene, "lever-base", "stone", 0.6, 0.6);
    base.isPickable = false;
    this.casters.push(base);
    const pivot = new TransformNode("lever-pivot", scene);
    pivot.position.set(at.x, at.y + 0.42, at.z);
    // Tilts towards / away from the arena centre.
    pivot.rotation.y = Math.atan2(at.x, at.z) - Math.PI / 2;
    const arm = CreateCylinder("lever-arm", { height: 1.2, diameter: 0.09, tessellation: 6 }, scene);
    arm.parent = pivot;
    arm.position.y = 0.6;
    arm.material = flatMaterial(scene, "lever-arm", "#3a3640");
    arm.isPickable = false;
    const knob = CreateSphere("lever-knob", { diameter: 0.24, segments: 6 }, scene);
    knob.parent = pivot;
    knob.position.y = 1.2;
    const knobMaterial = flatMaterial(scene, "lever-knob", "#c0302a");
    knobMaterial.emissiveColor = Color3.FromHexString("#401008");
    knob.material = knobMaterial;
    knob.isPickable = false;
    this.casters.push(arm);
    this.levers.push(pivot);
    const ring = CreateDisc("lever-ring", { radius: 1.1, tessellation: 32 }, scene);
    ring.rotation.x = Math.PI / 2;
    ring.position.set(at.x, at.y + 0.015, at.z);
    ring.material = this.ringMaterial;
    ring.isPickable = false;
    this.leverRings.push(ring);
  }

  /** The emperor's box on the north side: platform, golden columns, purple-red canopy. */
  private buildEmperorBox(scene: Scene, b: number): void {
    const d = PARAPET + TIER_RUN * 1.5;
    const y = this.oval.wall + TIER_RISE * 2;
    const z = b + d;
    const gold = flatMaterial(scene, "gold", "#d8a640");
    gold.emissiveColor = Color3.FromHexString("#2a1a04");
    const red = flatMaterial(scene, "imperial", "#8f1f33");
    const platform = CreateBox("box-platform", { width: 6, height: 0.5, depth: 3 }, scene);
    platform.position.set(0, y + 0.25, z + 0.6);
    platform.material = this.textured(scene, "box-platform", "stone", 2, 1);
    const cloth = CreateBox("box-cloth", { width: 6.1, height: 0.9, depth: 0.08 }, scene);
    cloth.position.set(0, y + 0.1, z - 0.92);
    cloth.material = red;
    for (const [cx, cz] of [[-2.7, -0.7], [2.7, -0.7], [-2.7, 1.8], [2.7, 1.8]] as const) {
      const column = CreateCylinder("box-column", { height: 2.6, diameter: 0.22, tessellation: 8 }, scene);
      column.position.set(cx, y + 0.5 + 1.3, z + 0.6 + cz);
      column.material = gold;
    }
    const canopy = CreateBox("box-canopy", { width: 6.4, height: 0.3, depth: 3.4 }, scene);
    canopy.position.set(0, y + 3.2, z + 0.6);
    canopy.material = red;
    for (const m of [platform, cloth, canopy]) m.isPickable = false;
    // Keeps the emperor's seat free of ordinary spectators.
    this.emperorSpot = { x: 0, z: z - 0.2, y: y + 0.5 };
  }

  private emperorSpot = { x: 0, z: 0, y: 0 };

  /** People of every age on the steps: one sprite each, from a small atlas drawn in code. */
  private buildCrowd(scene: Scene, a: number, b: number): void {
    const manager = new SpriteManager("crowd", crowdAtlas(), 2400, { width: 16, height: 24 }, scene, 0.01, Texture.NEAREST_SAMPLINGMODE);
    manager.isPickable = false;
    let seed = 1234;
    const random = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    for (let k = 0; k < TIERS; k++) {
      const d = PARAPET + TIER_RUN * (k + 0.55);
      const y = this.oval.wall + TIER_RISE * (k + 1);
      const ea = a + d;
      const eb = b + d;
      const perimeter = Math.PI * (3 * (ea + eb) - Math.sqrt((3 * ea + eb) * (ea + 3 * eb)));
      const count = Math.floor(perimeter / 0.62);
      for (let i = 0; i < count; i++) {
        if (random() < 0.14) continue;
        const t = ((i + random() * 0.4) / count) * Math.PI * 2;
        const x = Math.cos(t) * ea;
        const z = Math.sin(t) * eb;
        if (Math.abs(x - this.emperorSpot.x) < 3.4 && z > 0 && Math.abs(z - this.emperorSpot.z) < 3.2) continue;
        this.addSpectator(manager, x, y, z, 2 * Math.floor(random() * PEOPLE_VARIANTS), t, random);
      }
    }
    this.addSpectator(manager, this.emperorSpot.x, this.emperorSpot.y, this.emperorSpot.z, EMPEROR_CELL, Math.PI / 2, random);
  }

  private addSpectator(manager: SpriteManager, x: number, y: number, z: number, cell: number, angle: number, random: () => number): void {
    const sprite = new Sprite("fan", manager);
    sprite.width = 0.55;
    sprite.height = 0.82;
    const baseY = y + sprite.height / 2;
    sprite.position.set(x, baseY, z);
    sprite.cellIndex = cell;
    sprite.color = new Color4(1, 1, 1, 1);
    const turn = ((angle / (Math.PI * 2)) % 1 + 1) % 1;
    this.spectators.push({ sprite, baseY, phase: random() * 6.28, speed: 5 + random() * 6, cell, sector: Math.min(SECTORS - 1, Math.floor(turn * SECTORS)) });
  }
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** Steps of the stands, then the arcaded facade on top. */
function standsProfile(wall: number): { stands: ProfilePoint[]; facade: ProfilePoint[] } {
  const stands: ProfilePoint[] = [{ d: 0, y: wall }, { d: PARAPET, y: wall }];
  let d = PARAPET;
  let y = wall;
  for (let k = 0; k < TIERS; k++) {
    y += TIER_RISE;
    stands.push({ d, y });
    d += TIER_RUN;
    stands.push({ d, y });
  }
  return { stands, facade: [{ d, y }, { d, y: y + FACADE }] };
}

/**
 * A band around an ellipse: each profile point is a ring (the ellipse grown
 * by `d`, at height `y`), consecutive rings joined. `from`/`to` pick a slice
 * of the turn (0–1). UVs in metres, so the pixel textures keep their size.
 */
function ovalStrip(a: number, b: number, profile: ProfilePoint[], from: number, to: number, segments: number): VertexData {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const rows = profile.length;
  let v = 0;
  const vs: number[] = [0];
  for (let r = 1; r < rows; r++) {
    v += Math.hypot(profile[r]!.d - profile[r - 1]!.d, profile[r]!.y - profile[r - 1]!.y);
    vs.push(v);
  }
  for (let s = 0; s <= segments; s++) {
    const t = (from + ((to - from) * s) / segments) * Math.PI * 2;
    const meanA = a + 4;
    const u = (t * meanA) / 2;
    for (let r = 0; r < rows; r++) {
      const p = profile[r]!;
      positions.push(Math.cos(t) * (a + p.d), p.y, Math.sin(t) * (b + p.d));
      uvs.push(u, vs[r]! / 2);
    }
  }
  for (let s = 0; s < segments; s++) {
    for (let r = 0; r < rows - 1; r++) {
      const i = s * rows + r;
      const j = (s + 1) * rows + r;
      indices.push(i, j, i + 1, i + 1, j, j + 1);
    }
  }
  return withNormals(positions, uvs, indices);
}

/** The sand: from the edge of the hatch hole (or the centre) out to the ellipse. */
function sandMesh(oval: ArenaOval, hole: { min: { x: number; z: number }; max: { x: number; z: number } } | null): VertexData {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const RINGS = 8;
  const tile = SAND_TILE;
  const angles: number[] = [];
  for (let s = 0; s < SEGMENTS; s++) angles.push((s / SEGMENTS) * Math.PI * 2);
  if (hole) {
    // The corners of the hole exactly, so its edges stay straight.
    for (const [x, z] of [[hole.max.x, hole.max.z], [hole.min.x, hole.max.z], [hole.min.x, hole.min.z], [hole.max.x, hole.min.z]] as const) {
      angles.push((Math.atan2(z, x) + Math.PI * 2) % (Math.PI * 2));
    }
  }
  angles.sort((p, q) => p - q);
  for (const t of angles) {
    const cx = Math.cos(t);
    const cz = Math.sin(t);
    let ix = 0;
    let iz = 0;
    if (hole) {
      // Where the ray from the centre leaves the rectangle of the hole.
      const k = Math.min(cx !== 0 ? Math.abs(hole.max.x / cx) : Infinity, cz !== 0 ? Math.abs(hole.max.z / cz) : Infinity);
      ix = cx * k;
      iz = cz * k;
    }
    // Ellipse point along the same ray.
    const k = 1 / Math.sqrt((cx / oval.a) ** 2 + (cz / oval.b) ** 2);
    const ox = cx * k;
    const oz = cz * k;
    for (let r = 0; r <= RINGS; r++) {
      const f = r / RINGS;
      const x = ix + (ox - ix) * f;
      const z = iz + (oz - iz) * f;
      positions.push(x, 0.002, z);
      uvs.push(x / tile, z / tile);
    }
  }
  const n = angles.length;
  for (let s = 0; s < n; s++) {
    const next = (s + 1) % n;
    for (let r = 0; r < RINGS; r++) {
      const i = s * (RINGS + 1) + r;
      const j = next * (RINGS + 1) + r;
      indices.push(i, i + 1, j, i + 1, j + 1, j);
    }
  }
  return withNormals(positions, uvs, indices);
}

function withNormals(positions: number[], uvs: number[], indices: number[]): VertexData {
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.uvs = uvs;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  data.normals = normals;
  return data;
}

function bounds(boxes: ArenaBox[]): { min: { x: number; z: number }; max: { x: number; z: number } } | null {
  if (boxes.length === 0) return null;
  return {
    min: { x: Math.min(...boxes.map((b) => b.min.x)), z: Math.min(...boxes.map((b) => b.min.z)) },
    max: { x: Math.max(...boxes.map((b) => b.max.x)), z: Math.max(...boxes.map((b) => b.max.z)) },
  };
}

function angleDiff(p: number, q: number): number {
  let d = (p - q) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

function flatMaterial(scene: Scene, name: string, color: string): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.diffuseColor = Color3.FromHexString(color);
  material.specularColor = Color3.Black();
  return material;
}

// ---------------------------------------------------------------------------
// The crowd atlas: 16 × 24 px spectators, arms down and arms up, plus the emperor
// ---------------------------------------------------------------------------

const TUNICS = ["#b03a3a", "#3a6ab0", "#d8c070", "#e8e0d0", "#5a9a50", "#8a4ab0", "#c0702a", "#3a3a3a", "#2a8a8a", "#d07a9a", "#7a5a3a", "#a0a8b8"];
const SKINS = ["#f0c8a0", "#c89068", "#8a5a3a", "#e0b088", "#5a3a28", "#f4d4b4"];
const HAIR = ["#2a1a10", "#5a3a1a", "#c8a050", "#1a1a1a", "#8a8a8a", "#a04a20"];

/** The atlas as a data URL, for the sprite manager. */
function crowdAtlas(): string {
  const cells = EMPEROR_CELL + 1;
  const canvas = document.createElement("canvas");
  canvas.width = 16 * cells;
  canvas.height = 24;
  const g = canvas.getContext("2d")!;
  const person = (cell: number, tunic: string, skin: string, hair: string, armsUp: boolean, crown = false) => {
    const ox = cell * 16;
    g.fillStyle = tunic;
    g.fillRect(ox + 4, 10, 8, 14);
    g.fillStyle = "rgba(0,0,0,0.25)";
    g.fillRect(ox + 4, 18, 8, 6);
    g.fillStyle = skin;
    g.fillRect(ox + 5, 3, 6, 7);
    g.fillStyle = hair;
    g.fillRect(ox + 5, 2, 6, 3);
    g.fillStyle = "#1a1012";
    g.fillRect(ox + 6, 6, 1, 1);
    g.fillRect(ox + 9, 6, 1, 1);
    g.fillStyle = skin;
    if (armsUp) {
      g.fillRect(ox + 2, 1, 2, 9);
      g.fillRect(ox + 12, 1, 2, 9);
    } else {
      g.fillRect(ox + 2, 11, 2, 7);
      g.fillRect(ox + 12, 11, 2, 7);
    }
    if (crown) {
      g.fillStyle = "#5ac040";
      g.fillRect(ox + 4, 1, 8, 2);
    }
  };
  for (let v = 0; v < PEOPLE_VARIANTS; v++) {
    const tunic = TUNICS[v % TUNICS.length]!;
    const skin = SKINS[(v * 5) % SKINS.length]!;
    const hair = HAIR[(v * 7) % HAIR.length]!;
    person(v * 2, tunic, skin, hair, false);
    person(v * 2 + 1, tunic, skin, hair, true);
  }
  person(EMPEROR_CELL, "#6a2a7a", "#f0c8a0", "#d8c070", false, true);
  return canvas.toDataURL("image/png");
}
